import axios from 'axios';
import { authService } from './authServiceBFF';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor for CSRF token
api.interceptors.request.use(
  async (config) => {
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      let csrfToken = authService.getCachedCsrfToken();
      if (!csrfToken) {
        csrfToken = await authService.getCsrfToken();
      }
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export interface SurveyDashboard {
  totalSurveys: number;
  sectionsWithData: number;
  estimateAccuracy: number;
  problemSections: number;
  averageRating: number;
  completedSections: number;
  skippedSections: number;
  lastSurveyDate?: string;
  difficultyDistribution: Record<string, number>;
  topTrailConditions: Record<string, number>;
}

export interface SectionSummary {
  gisId: string;
  sectionName: string;
  responseCount: number;
  currentEstimateHours?: number;
  averageActualTimeHours?: number;
  estimateAccuracy?: number;
  averageRating?: number;
  difficultyDistribution: Record<string, number>;
  skipRate: number;
  isProblemSection: boolean;
  problemReasons: string[];
  lastResponseDate?: string;
}

export interface SectionSummaryList {
  sections: SectionSummary[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface SurveyResponse {
  id: string;
  userId?: string;
  userName?: string;
  surveyDate: string;
  actualTimeHours?: number;
  estimatedTimeHours?: number;
  overallRating?: number;
  difficultyRating?: string;
  trailConditions: string[];
  skipReasons: string[];
  commentForTaTrust?: string;
  isSkipped: boolean;
  estimateAccuracy?: number;
  location?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  };
}

export interface TimeEstimateAnalysis {
  currentEstimate?: number;
  medianActualTime?: number;
  meanActualTime?: number;
  standardDeviation?: number;
  minActualTime?: number;
  maxActualTime?: number;
  sampleSize: number;
  recommendedEstimate?: number;
  percentWithin25Percent: number;
}

export interface SectionDetail {
  gisId: string;
  sectionName: string;
  summary: SectionSummary;
  responses: SurveyResponse[];
  timeAnalysis: TimeEstimateAnalysis;
  commonTrailConditions: string[];
  commonSkipReasons: string[];
  recommendedEstimateHours?: string;
  userComments: Array<{
    userId?: string;
    userName?: string;
    comment: string;
    date: string;
  }>;
}

export interface ProblemSection {
  gisId: string;
  sectionName: string;
  issues: string[];
  estimateAccuracy?: number;
  averageRating?: number;
  skipRate?: number;
  responseCount: number;
  priority: 'High' | 'Medium' | 'Low';
}

export interface SurveyFilters {
  sortBy?: string;
  descending?: boolean;
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  minAccuracy?: number;
  maxAccuracy?: number;
  minRating?: number;
  maxRating?: number;
}

class SurveyService {
  async getDashboard(): Promise<SurveyDashboard> {
    const response = await api.get<SurveyDashboard>('/api/survey-data/dashboard');
    return response.data;
  }

  async getSectionSummaries(filters: SurveyFilters = {}): Promise<SectionSummaryList> {
    const params = new URLSearchParams();
    
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.descending !== undefined) params.append('descending', filters.descending.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.minAccuracy !== undefined) params.append('minAccuracy', filters.minAccuracy.toString());
    if (filters.maxAccuracy !== undefined) params.append('maxAccuracy', filters.maxAccuracy.toString());
    if (filters.minRating !== undefined) params.append('minRating', filters.minRating.toString());
    if (filters.maxRating !== undefined) params.append('maxRating', filters.maxRating.toString());

    const response = await api.get<SectionSummaryList>(`/api/survey-data/sections?${params.toString()}`);
    return response.data;
  }

  async getSectionDetail(gisId: string): Promise<SectionDetail> {
    const response = await api.get<SectionDetail>(`/api/survey-data/sections/${gisId}`);
    return response.data;
  }

  async getProblemSections(): Promise<ProblemSection[]> {
    const response = await api.get<ProblemSection[]>('/api/survey-data/problem-sections');
    return response.data;
  }
}

export const surveyService = new SurveyService();